import React, { useEffect, useState, useMemo } from 'react';
import { useAuth, apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { BarChart3, Filter, Users, TrendingUp, DollarSign, Target, Loader2 } from 'lucide-react';
import logger from '@/lib/logger';

// All stages that can appear in a lead pipeline. Listed here so the
// table always renders a stable column order even if a campaign has
// zero leads in a particular stage. Mirrors the backend's stage enum.
const STAGE_COLUMNS = [
  { id: 'new_lead', label: 'New', color: 'bg-blue-500/15 text-blue-700 dark:text-blue-300' },
  { id: 'call_back', label: 'Call Back', color: 'bg-purple-500/15 text-purple-700 dark:text-purple-300' },
  { id: 'warm_lead', label: 'Warm', color: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-300' },
  { id: 'hot_lead', label: 'Hot', color: 'bg-orange-500/15 text-orange-700 dark:text-orange-300' },
  { id: 'enrolled', label: 'Enrolled', color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  { id: 'rejected', label: 'Rejected', color: 'bg-rose-500/15 text-rose-700 dark:text-rose-300' },
];

const PRESET_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'this_year', label: 'This year' },
  { value: 'last_year', label: 'Last year' },
  { value: 'custom', label: 'Custom range' },
];

const fmtCurrency = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, { style: 'currency', currency: 'AED', maximumFractionDigits: 0 });
};

const ALLOWED = ['super_admin', 'admin', 'marketing', 'marketing_admin', 'sales_manager', 'coo', 'ceo'];

export default function LeadAnalysisPage() {
  const { user } = useAuth();
  const isAllowed = ALLOWED.includes(user?.role);

  const [preset, setPreset] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Drill-through state: which (campaign, stage) cell was clicked.
  const [drill, setDrill] = useState(null);          // { campaign, stage }
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const queryParams = useMemo(() => {
    if (preset === 'custom') {
      const p = new URLSearchParams();
      if (customFrom) p.set('date_from', new Date(customFrom).toISOString());
      if (customTo) {
        // Make `date_to` exclusive end-of-day
        const d = new Date(customTo); d.setDate(d.getDate() + 1);
        p.set('date_to', d.toISOString());
      }
      return p.toString();
    }
    return `preset=${preset}`;
  }, [preset, customFrom, customTo]);

  useEffect(() => {
    if (!isAllowed) return;
    if (preset === 'custom' && (!customFrom || !customTo)) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await apiClient.get(`/analytics/lead-analysis?${queryParams}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        logger.error('Lead analysis fetch failed:', e);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [queryParams, isAllowed, preset, customFrom, customTo]);

  // Drill-through handler — fired when a cell in the by-stage matrix is clicked.
  const openDrill = async (campaign, stage) => {
    setDrill({ campaign, stage });
    setDrillLoading(true);
    setDrillData(null);
    try {
      const p = new URLSearchParams(queryParams);
      p.set('campaign', campaign);
      if (stage) p.set('stage', stage);
      const r = await apiClient.get(`/analytics/lead-analysis/agents?${p.toString()}`);
      setDrillData(r.data);
    } catch (e) {
      logger.error('Drill fetch failed:', e);
    } finally {
      setDrillLoading(false);
    }
  };

  if (!isAllowed) {
    return (
      <div className="p-6">
        <Card className="max-w-md mx-auto mt-12">
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            Lead Analysis is restricted to managers and admins.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5" data-testid="lead-analysis-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight inline-flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-indigo-500" />
            Lead Analysis
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Campaign-by-campaign breakdown of every lead in the chosen window.
            Click any number to see which agents are sitting on those leads —
            so you can tell at a glance which campaigns are working and which
            need to be killed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-44" data-testid="lead-analysis-preset">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESET_OPTIONS.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {preset === 'custom' && (
            <>
              <Input type="date" value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-40" data-testid="lead-analysis-from" />
              <Input type="date" value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-40" data-testid="lead-analysis-to" />
            </>
          )}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryTile icon={Target} label="Total leads"
          value={data?.total_leads ?? '—'} loading={loading} tone="indigo" />
        <SummaryTile icon={TrendingUp} label="Enrolled"
          value={data?.total_enrolled ?? '—'} loading={loading} tone="emerald" />
        <SummaryTile icon={DollarSign} label="Revenue"
          value={data ? fmtCurrency(data.total_revenue) : '—'} loading={loading} tone="amber" />
        <SummaryTile icon={BarChart3} label="Conversion"
          value={data ? `${data.conversion_rate}%` : '—'} loading={loading} tone="rose" />
      </div>

      {/* Campaigns table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Campaigns</CardTitle>
            {data?.campaigns?.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {data.campaigns.length} campaign{data.campaigns.length === 1 ? '' : 's'} ·
                click any cell to see which agents own those leads
              </p>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !data?.campaigns?.length ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No leads in this window.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="lead-analysis-table">
              <thead className="bg-muted/40 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Campaign / Source</th>
                  <th className="text-right px-3 py-2 font-medium">Total</th>
                  {STAGE_COLUMNS.map(s => (
                    <th key={s.id} className="text-right px-3 py-2 font-medium">{s.label}</th>
                  ))}
                  <th className="text-right px-4 py-2 font-medium">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {data.campaigns.map((c) => (
                  <tr key={c.campaign} className="border-t hover:bg-muted/30">
                    <td className="px-4 py-2 font-medium truncate max-w-[260px]" title={c.campaign}>
                      {c.campaign}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => openDrill(c.campaign, null)}
                        className="font-semibold text-foreground hover:underline"
                        data-testid={`drill-${c.campaign}-total`}
                      >
                        {c.total}
                      </button>
                    </td>
                    {STAGE_COLUMNS.map(s => {
                      const n = c.by_stage[s.id] || 0;
                      return (
                        <td key={s.id} className="px-3 py-2 text-right">
                          {n > 0 ? (
                            <button
                              onClick={() => openDrill(c.campaign, s.id)}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium hover:ring-1 hover:ring-offset-1 ring-offset-background transition ${s.color}`}
                              data-testid={`drill-${c.campaign}-${s.id}`}
                            >
                              {n}
                            </button>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2 text-right font-mono text-xs">
                      {c.revenue > 0 ? fmtCurrency(c.revenue) : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Drill-through dialog */}
      <Dialog open={!!drill} onOpenChange={(o) => { if (!o) { setDrill(null); setDrillData(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="lead-analysis-drill-dialog">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <Users className="h-5 w-5 text-indigo-500" />
              {drill?.campaign || ''}
              {drill?.stage && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-normal">
                  {STAGE_COLUMNS.find(s => s.id === drill.stage)?.label || drill.stage}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Per-agent breakdown for this campaign{drill?.stage ? ' and stage' : ''}.
              Click an agent to drill deeper into their pipeline (coming soon).
            </DialogDescription>
          </DialogHeader>
          {drillLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
              Loading…
            </div>
          ) : !drillData?.agents?.length ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No agents — these leads aren&apos;t assigned to anyone.
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Agent</th>
                    <th className="text-right px-3 py-2 font-medium">Leads</th>
                    <th className="text-right px-3 py-2 font-medium">Enrolled</th>
                    <th className="text-right px-4 py-2 font-medium">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {drillData.agents.map((a) => (
                    <tr key={a.agent} className="border-t hover:bg-muted/20">
                      <td className="px-4 py-2 font-medium">{a.agent}</td>
                      <td className="px-3 py-2 text-right">{a.count}</td>
                      <td className="px-3 py-2 text-right text-emerald-600 font-medium">{a.enrolled || '—'}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs">
                        {a.revenue > 0 ? fmtCurrency(a.revenue) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, loading, tone }) {
  const tones = {
    indigo: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 ring-indigo-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-500/20',
    amber: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-amber-500/20',
    rose: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 ring-rose-500/20',
  };
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 rounded-lg ring-1 ring-inset flex items-center justify-center ${tones[tone] || tones.indigo}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
            <p className="text-2xl font-semibold tabular-nums mt-0.5">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : value}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
